import { taskNotificationHref } from "./task-notification-link";
import { supabase } from "@/integrations/supabase/client";
import { createNotification, type CreateNotificationInput } from "@/api/notifications";

/**
 * Looks up a person by their user_id (from auth.users)
 * Tasks use assignee_id (user_id) but notifications need person_id
 */
export async function getPersonByUserId(
  userId: string,
  organizationId: string
): Promise<{ id: string; email: string | null; first_name: string | null; last_name: string | null } | null> {
  const { data, error } = await supabase
    .from("people")
    .select("id, email, first_name, last_name")
    .eq("user_id", userId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) {
    console.error("Error looking up person by user_id:", error);
    return null;
  }

  return data;
}

export interface CreateTaskAssignmentNotificationOptions {
  organizationId: string;
  taskId: string;
  taskTitle: string;
  taskDescription?: string | null;
  assigneeUserId: string;
  actorPersonId: string;
  actorName: string;
  previousAssigneeUserId?: string | null;
}

/**
 * Creates a notification when a task is assigned to someone
 * Handles both new assignments and reassignments
 */
export async function createTaskAssignmentNotification(
  options: CreateTaskAssignmentNotificationOptions
): Promise<void> {
  const {
    organizationId,
    taskId,
    taskTitle,
    taskDescription,
    assigneeUserId,
    actorPersonId,
    actorName,
    previousAssigneeUserId,
  } = options;

  // Don't notify if someone assigns a task to themselves
  const assigneePerson = await getPersonByUserId(assigneeUserId, organizationId);
  if (!assigneePerson) {
    console.warn(`Could not find person for assignee user_id: ${assigneeUserId}`);
    return;
  }

  // Skip notification if the actor is assigning to themselves
  if (assigneePerson.id === actorPersonId) {
    return;
  }

  const isReassignment = !!previousAssigneeUserId;
  const title = isReassignment
    ? `${actorName} reassigned a task to you`
    : `${actorName} assigned a task to you`;

  const body = taskDescription && taskDescription.length > 100
    ? `${taskDescription.substring(0, 100)}...`
    : taskDescription || undefined;

  const notification: CreateNotificationInput = {
    organizationId,
    personId: assigneePerson.id,
    actorPersonId,
    title,
    body,
    linkUrl: taskNotificationHref(taskId, organizationId),
    linkLabel: `View "${taskTitle}"`,
    notificationType: "assignment",
    metadata: {
      task_id: taskId,
      task_title: taskTitle,
      is_reassignment: isReassignment,
      previous_assignee_user_id: previousAssigneeUserId,
    },
  };

  try {
    await createNotification(notification);
  } catch (error) {
    console.error("Failed to create task assignment notification:", error);
    // Don't throw - notification creation is not critical
  }
}
